/**
 * GA4 Direct Fallbacks
 *
 * When CDN's page-type-gated GA4 functions (gaViewItem, gaViewItemList,
 * gaVariantChange, gaViewCart, gaSearch) aren't available in headless SPA,
 * these helpers replicate the CDN's sendMultipleDests + sendGA4Event pattern
 * by calling gtag() directly with send_to targeting.
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';

/**
 * Send GA4 event to primary and secondary GA4 properties.
 * Replicates CDN's sendMultipleDests → sendGA4Event pattern.
 */
export function sendGA4Fallback(
  analyzify: WindowAnalyzify,
  eventName: string,
  eventParams: Record<string, any>
): void {
  const win = window as any;
  if (typeof win.gtag !== 'function') return;

  const props = analyzify.properties;
  if (!props?.GA4?.status) return;

  const shop = analyzify.shop;
  const market = analyzify.market;

  const commonFields = {
    cart_id: analyzify.cart_id || null,
    shop_locale: shop?.locale || null,
    shop_country: shop?.country || null,
    market_country: market?.country || null,
    market_language: market?.language || null,
    market_handle: market?.handle || null,
  };

  // Primary GA4 property
  const primary = props.GA4.primary;
  if (primary?.status && (primary.events as any)?.[eventName]) {
    const params = remapItemIds(analyzify, eventParams, primary.product_id_format);
    win.gtag('event', eventName, {
      ...params,
      ...commonFields,
      send_to: primary.id,
    });
  }

  // Secondary GA4 property
  const secondary = props.GA4.secondary;
  if (secondary?.status && (secondary.events as any)?.[eventName]) {
    const params = remapItemIds(analyzify, eventParams, secondary.product_id_format);
    win.gtag('event', eventName, {
      ...params,
      ...commonFields,
      send_to: secondary.id,
    });
  }
}

/**
 * Re-resolve item `id` fields for a specific destination's product_id_format.
 * CDN maps items per-destination inside sendGA4Event; this replicates that by
 * using the already-mapped item_id, item_variant_id, item_sku fields.
 */
function remapItemIds(
  analyzify: WindowAnalyzify,
  eventParams: Record<string, any>,
  format?: string
): Record<string, any> {
  if (!eventParams.items || !Array.isArray(eventParams.items)) {
    return eventParams;
  }
  return {
    ...eventParams,
    items: eventParams.items.map((item: any) => {
      const { _productId, ...clean } = item;
      if (!format) return clean;
      const resolved = resolveGA4ItemId(analyzify, _productId, item.item_variant_id, item.item_sku, format);
      return { ...clean, id: resolved, item_id: resolved };
    }),
  };
}

/**
 * Resolve the GA4 item `id` field based on configured product_id_format.
 * Matches CDN's getItemIds logic which respects ga4Props.product_id_format.
 *
 * For product-level items: productId = product.id, variantId = variant.id
 * For cart-level items: productId = item.product_id, variantId = item.variant_id
 */
function resolveGA4ItemId(
  analyzify: WindowAnalyzify,
  productId: string | number | null | undefined,
  variantId: string | number | null | undefined,
  sku: string | null | undefined,
  format?: string
): string | null {
  const idFormat = format || analyzify.properties?.GA4?.primary?.product_id_format || 'shopify_item_id';

  const shopifyId = analyzify.getShopifyId?.(
    analyzify.feed_region || '',
    productId ?? '',
    variantId ?? '',
    sku ?? ''
  ) || null;

  // Mirror an_analyzify.js getItemIds: idMap[format] || default(product_id) || sku || variant_id
  const idMap: Record<string, any> = {
    product_sku: sku,
    variant_id: variantId,
    shopify_item_id: shopifyId,
    default: productId,
  };

  const selected = idMap[idFormat] || idMap['default'] || sku || variantId;
  return selected != null ? selected.toString() : null;
}

/**
 * Map product + variant to GA4 item format matching CDN's gaItemMapper output.
 * Used for product-level items (view_item, view_item_list, search).
 */
export function mapGA4Item(
  analyzify: WindowAnalyzify,
  product: any,
  variant: any,
  index: number,
  listId?: string | number | null,
  listName?: string | null
): Record<string, any> {
  const price = analyzify.formatPrice?.(variant?.price ?? product?.price, true) || 0;
  const compareAt = variant?.compare_at_price
    ? (analyzify.formatPrice?.(variant.compare_at_price, true) || 0)
    : 0;
  const discount = (compareAt > 0 && compareAt !== price) ? Math.abs(compareAt - price) : 0;

  const selectedId = resolveGA4ItemId(analyzify, product?.id, variant?.id, variant?.sku);

  return {
    id: selectedId,
    item_id: selectedId,
    // Raw IDs preserved for per-destination remapping in sendGA4Fallback
    _productId: product?.id?.toString() || null,
    item_name: (product?.product_title || product?.title)?.trim() || null,
    item_brand: product?.vendor?.trim() || null,
    item_category: (product?.type || product?.product_type)?.trim() || null,
    item_sku: variant?.sku?.toString() || null,
    item_handle: product?.handle || null,
    discount: analyzify.formatPrice?.(discount, false) || 0,
    item_barcode: variant?.barcode || null,
    quantity: Number(product?.quantity) || 1,
    item_variant: variant?.title || product?.variant_title || null,
    item_variant_id: variant?.id?.toString() || null,
    index,
    item_list_id: listId?.toString() || null,
    item_list_name: listName?.trim() || null,
    price,
    ...(analyzify.properties?.GA4?.enhanced_params ? {
      tags: product?.tags?.join(',') || null,
      options: product?.options?.join(',') || null,
      variant_ids: product?.variants?.map((v: any) => v?.id?.toString() || null).join(',') || null,
    } : {}),
  };
}

/**
 * Map cart item to GA4 item format matching CDN's gaItemMapper output.
 * Used for cart-level items (view_cart).
 */
export function mapGA4CartItem(
  analyzify: WindowAnalyzify,
  item: any,
  index: number
): Record<string, any> {
  const price = analyzify.formatPrice?.(item.price, true) ?? item.price / 100;
  const discount = item.line_level_total_discount
    ? (analyzify.formatPrice?.(item.line_level_total_discount, true) || 0)
    : 0;

  const selectedId = resolveGA4ItemId(analyzify, item.product_id, item.variant_id, item.sku);

  return {
    id: selectedId,
    item_id: selectedId,
    // Raw IDs preserved for per-destination remapping in sendGA4Fallback
    _productId: item.product_id?.toString() || null,
    item_name: item.product_title?.trim() || item.title?.trim() || null,
    item_brand: item.vendor?.trim() || null,
    item_category: item.product_type || null,
    item_sku: item.sku?.toString() || null,
    item_handle: item.handle || null,
    discount,
    item_barcode: null,
    quantity: item.quantity || 1,
    item_variant: item.variant_title?.trim() || null,
    item_variant_id: item.variant_id?.toString() || null,
    index,
    item_list_id: null,
    item_list_name: null,
    price,
    // Mirror CDN gaItemMapper: enhanced_params apply to cart items too.
    // Cart items lack a `variants` array, so variant_ids falls back to variant_id.
    ...(analyzify.properties?.GA4?.enhanced_params ? {
      tags: item.tags?.join(',') || null,
      options: item.options?.join(',') || item.variant_options?.join(',') || null,
      variant_ids: item.variants?.map((v: any) => v?.id?.toString() || null).join(',') || item.variant_id?.toString() || null,
    } : {}),
  };
}
